import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatValueAuto, formatIndianInt } from './fieldSalesFormat.js';

// Reference tokens.
const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const BAD = '#DC2626';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';

const NO_DATA_TOOLTIP = 'No data source yet';

// Fiscal month order: month_no 1 = Apr … 12 = Mar.
const FISCAL_LABELS = [
  'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar',
];
// Quarters by month_no (1-based): Q1 1-3, Q2 4-6, Q3 7-9, Q4 10-12.
const QUARTERS = [
  { label: 'Q1', months: [1, 2, 3] },
  { label: 'Q2', months: [4, 5, 6] },
  { label: 'Q3', months: [7, 8, 9] },
  { label: 'Q4', months: [10, 11, 12] },
];

/** ₹ Cr/L (lakh input), em dash for non-positive (reference `val`). */
const val = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

/** Percentage of a base, N decimals; em dash when base <= 0. */
function pctOf(v, base, dec = 1) {
  if (!(Number(base) > 0)) return '—';
  return `${((Number(v) / Number(base)) * 100).toFixed(dec)}%`;
}

/** Achievement %: CY/Target, coloured by tone (reference achCell). */
function achText(cy, tg) {
  if (!(Number(cy) > 0 && Number(tg) > 0)) return { text: '—', color: MUTED };
  const p = (Number(cy) / Number(tg)) * 100;
  return { text: `${p.toFixed(1)}%`, color: p >= 100 ? GOOD : p >= 80 ? MUTED : BAD };
}

/** Growth %: (CY-LY)/LY, signed + coloured (reference grCell). */
function grText(cy, ly) {
  if (!(Number(cy) > 0 && Number(ly) > 0)) return { text: '—', color: MUTED };
  const g = ((Number(cy) - Number(ly)) / Number(ly)) * 100;
  return { text: `${g > 0 ? '+' : ''}${g.toFixed(1)}%`, color: g > 0 ? GOOD : g < 0 ? BAD : MUTED };
}

const SUM_FIELDS = ['ly', 'tgt', 'cy', 'sec', 'expiry', 'sales_return', 'rate_diff'];

/** Sum the SUM_FIELDS across a list of series points → a column aggregate. */
function aggregate(points) {
  const out = { ly: 0, tgt: 0, cy: 0, sec: 0, expiry: 0, sales_return: 0, rate_diff: 0 };
  for (const p of points) {
    for (const f of SUM_FIELDS) out[f] += Number(p?.[f]) || 0;
  }
  return out;
}

const HEAD_SX = {
  bgcolor: NAVY,
  color: '#fff',
  fontWeight: 700,
  fontSize: '13px',
  letterSpacing: '.4px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  borderColor: '#1b2c50',
};
const LBL_HEAD_SX = { ...HEAD_SX, textAlign: 'left' };
const LBL_SX = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  textAlign: 'left',
  fontWeight: 600,
  color: NAVY,
  bgcolor: '#fff',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};
const NUM_SX = { textAlign: 'right', fontSize: '13px', whiteSpace: 'nowrap', color: '#1B2433' };
// Quarter / total column tints (reference .trend-q / .trend-tot).
const Q_SX = { ...NUM_SX, bgcolor: '#eef4ff', fontWeight: 700 };
const TOT_SX = { ...NUM_SX, bgcolor: '#dbe8ff', fontWeight: 800 };

/** Greyed "—" with a "No data" tag + tooltip, for the no-source PCPM row. */
function NoData() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.5, cursor: 'not-allowed' }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Monthly Trend tab (reference "Apr → Mar Trend"). The chart was removed; this
 * is the reference's metric TABLE only: rows (LY Sales/Target/CY Sales/Ach %/
 * Growth %/Secondary/Sec. Vs. Prim %/PCPM/Expiry/Expiry %/Sales Return/Sales
 * Return %/Rate Diff/Rate Diff %) across month columns with quarter subtotals
 * and a YTD total column, plus a TM count row. PCPM has no BO source and is
 * greyed. Values in Lakhs → ₹ Cr/L.
 *
 * @param {{
 *   query: import('@tanstack/react-query').UseQueryResult,
 *   tmTotal?: number,
 * }} props  tmTotal = the summary TM total, used in the PCPM subheading note.
 */
export default function FieldSalesTrendTab({ query, tmTotal }) {
  const data = query?.data;
  const series = Array.isArray(data?.series) ? data.series : [];

  // Index series by fiscal month_no (1..12).
  const byMonth = new Map(series.map((p) => [Number(p.month_no), p]));
  const monthPoints = Array.from({ length: 12 }, (_, i) => byMonth.get(i + 1) || null).filter(Boolean);

  // Table columns: 3 months + quarter subtotal per quarter, then a YTD column.
  const lastCyIdx = monthPoints.reduce((last, p) => (Number(p.cy) > 0 ? Number(p.month_no) : last), 0);
  const ytdMonths = lastCyIdx > 0 ? Array.from({ length: lastCyIdx }, (_, i) => i + 1) : Array.from({ length: 12 }, (_, i) => i + 1);
  const ytdLabel = lastCyIdx > 0 ? `YTD ${FISCAL_LABELS[lastCyIdx - 1]}` : 'YTD';

  const columns = [];
  for (const q of QUARTERS) {
    for (const mNo of q.months) {
      const p = byMonth.get(mNo);
      columns.push({ label: FISCAL_LABELS[mNo - 1], agg: aggregate(p ? [p] : []), tmCount: Number(p?.tm_count) || 0, kind: 'month' });
    }
    const qPoints = q.months.map((mNo) => byMonth.get(mNo)).filter(Boolean);
    const qTm = qPoints.reduce((s, p) => Math.max(s, Number(p?.tm_count) || 0), 0);
    columns.push({ label: q.label, agg: aggregate(qPoints), tmCount: qTm, kind: 'q' });
  }
  const ytdPoints = ytdMonths.map((mNo) => byMonth.get(mNo)).filter(Boolean);
  const ytdTm = ytdPoints.reduce((s, p) => Math.max(s, Number(p?.tm_count) || 0), 0);
  columns.push({ label: ytdLabel, agg: aggregate(ytdPoints), tmCount: ytdTm, kind: 'tot' });

  const colSx = (kind) => (kind === 'tot' ? TOT_SX : kind === 'q' ? Q_SX : NUM_SX);

  // Metric rows: label + a per-column cell renderer. PCPM is greyed (no BO).
  const metricRows = [
    { label: 'LY Sales', cell: (c) => val(c.agg.ly) },
    { label: 'Target', cell: (c) => val(c.agg.tgt) },
    { label: 'CY Sales', cell: (c) => val(c.agg.cy) },
    { label: 'Ach %', cell: (c) => { const a = achText(c.agg.cy, c.agg.tgt); return <Box component="span" sx={{ color: a.color }}>{a.text}</Box>; } },
    { label: 'Growth %', cell: (c) => { const g = grText(c.agg.cy, c.agg.ly); return <Box component="span" sx={{ color: g.color }}>{g.text}</Box>; } },
    { label: 'Secondary', cell: (c) => val(c.agg.sec) },
    { label: 'Sec. Vs. Prim %', cell: (c) => pctOf(c.agg.sec, c.agg.cy, 0) },
    { label: 'PCPM', cell: () => <NoData />, greyed: true },
    { label: 'Expiry', cell: (c) => val(c.agg.expiry) },
    { label: 'Expiry %', cell: (c) => pctOf(c.agg.expiry, c.agg.cy) },
    { label: 'Sales Return', cell: (c) => val(c.agg.sales_return) },
    { label: 'Sales Return %', cell: (c) => pctOf(c.agg.sales_return, c.agg.cy) },
    { label: 'Rate Diff', cell: (c) => val(c.agg.rate_diff) },
    { label: 'Rate Diff %', cell: (c) => pctOf(c.agg.rate_diff, c.agg.cy) },
    { label: 'TM count', cell: (c) => formatIndianInt(c.tmCount) },
  ];

  const loading = query?.isLoading || query?.isFetching;
  const anyData = series.length > 0;
  const tmText = Number.isFinite(Number(tmTotal)) && Number(tmTotal) > 0 ? formatIndianInt(tmTotal) : '—';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', mb: '6px' }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>
          📈 Apr → Mar Trend — LY · Target · CY · Secondary &amp; Achievement %
        </Typography>
      </Box>
      {/* Reference subheading (verbatim), with {TM count} from the summary TM total. */}
      <Typography sx={{ fontSize: '12px', color: MUTED, mb: '10px', lineHeight: 1.5 }}>
        Full fiscal-year view (Apr→Mar) — not affected by the Period selector; use the filters &amp; HQ
        slicer to change the data. Measure: Value · PCPM = CY Sales ÷ {tmText} BO ÷ (no. of months with CY
        sales in that column).
      </Typography>

      {query?.isError ? (
        <Alert severity="error">Failed to load the trend.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !anyData ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No data for the current selection.
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 520, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={LBL_HEAD_SX}>Metric</TableCell>
                {columns.map((c, i) => (
                  <TableCell key={`${c.label}-${i}`} sx={HEAD_SX}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metricRows.map((r) => (
                <TableRow key={r.label} sx={r.greyed ? { opacity: 0.5 } : undefined}>
                  <TableCell sx={LBL_SX}>{r.label}</TableCell>
                  {columns.map((c, i) => (
                    <TableCell key={`${r.label}-${i}`} sx={colSx(c.kind)}>
                      {r.cell(c)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
